import React, { FC, MouseEvent, useCallback, useEffect, useMemo } from "react";
import { ButtonColor } from "enums/Button";
import { IconType } from "enums/Icon";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { List, Map } from "immutable";
import { isEmpty } from "lodash";
import queryString from "query-string";
import { Col, Container, Hidden, Row, Visible } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { CartResponseDto } from "types/api/cart";
import { OutgoingResponseDto } from "types/api/outgoing";
import { OpenRightPanel } from "types/common/rightPanel";
import { OutgoingRowLayoutTier } from "types/outgoing";
import Icon from "../../components/Icon/Icon";
import LoadingBar from "../../components/LoadingBar/LoadingBar";
import { useDemoMode } from "../../contexts/DemoModeContext";
import { useStripePaymentReturn } from "../../hooks/useStripePaymentReturn";
import injectReducer from "../../reduxFolder/injectReducer";
import { getCart } from "../../reduxFolder/reducers/cart";
import reducer, { getOutgoing } from "../../reduxFolder/reducers/outgoing";
import {
  completeAllOverpay,
  completeOverpayPayment,
} from "../../reduxFolder/reducers/purchase";
import { openRightPanel } from "../../reduxFolder/reducers/rightPanel";
import { getAllDeliveryMethods } from "../../reduxFolder/reducers/services";
import {
  cartSelectForOutgoing,
  cartSelectLoading,
} from "../../reduxFolder/selectors/cartSelectors";
import { authSelectUnits } from "../../reduxFolder/selectors/globalSelectors";
import {
  outgoingSelectInformationData,
  outgoingSelectLoading,
  outgoingSelectPackingData,
  outgoingSelectPaidData,
  outgoingSelectPaymentData,
} from "../../reduxFolder/selectors/outgoingSelectors";
import { panelSelectOpened } from "../../reduxFolder/selectors/rightPanelSelectors";
import { servicesSelectAllDelivery } from "../../reduxFolder/selectors/servicesSelectors";
import { HeaderTitle } from "../../styles/parcelsStyles";
import formatter from "../../utils/formatter";
import { toastResponseError } from "../../utils/responseMessageHelper";
import DemoOutgoing from "./components/DemoOutgoing/DemoOutgoing";
import InProgress from "./components/InProgress/InProgress";
import NoItemsPlaceholder from "./components/NoItemsPlaceholder";
import NotFinished from "./components/NotFinished/NotFinished";
import OutgoingSection from "./components/OutgoingSection/OutgoingSection";
import Paid from "./components/Paid/Paid";
import WaitingForInformation from "./components/WaitingFor/WaitingForInformation";
import WaitingForPayment from "./components/WaitingFor/WaitingForPayment";
import {
  OutgoingWrapper,
  PayAllHeaderButton,
  PaymentHeaderContent,
} from "./Outgoing.styles";
import { refreshOutgoingPaymentData } from "./paymentRefresh";

interface OutgoingProps {
  units: string;
  isLoading: boolean;
  isCartLoading: boolean;
  isPanelOpened: boolean;
  allDeliveryMethods: any;
  paidItemsData: any;
  paymentItemsData: any;
  informationItemsData: any;
  packingItemsData: any;
  userCart: any;
  getCart: () => Promise<CartResponseDto>;
  openRightPanel: OpenRightPanel;
  getAllDeliveryMethods: () => Promise<void>;
  getOutgoing: (data?: any) => Promise<OutgoingResponseDto>;
  completeAllOverpay: () => Promise<void>;
  completeOverpayPayment: (data: any) => Promise<void>;
}

const Outgoing: FC<OutgoingProps> = React.memo(
  ({
    paymentItemsData,
    informationItemsData,
    packingItemsData,
    paidItemsData,
    openRightPanel,
    getOutgoing,
    isLoading,
    isCartLoading,
    isPanelOpened,
    getCart,
    userCart,
    units,
    getAllDeliveryMethods,
    allDeliveryMethods,
    completeOverpayPayment,
    completeAllOverpay,
  }) => {
    const params = useParams();
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const { isDemoEnabled } = useDemoMode();
    const navigate = useNavigate();
    const location = useLocation();
    const hasItems = !!(
      paymentItemsData.get("items").size ||
      informationItemsData.get("items").size ||
      packingItemsData.get("items").size ||
      userCart.get("items", List()).size ||
      paidItemsData.get("items", List()).size
    );
    // Demo renders only once every bucket AND the cart are loaded-and-empty
    // (outgoingSelectLoading covers only payment/information/packing).
    const isSettledEmpty =
      !isLoading &&
      !isCartLoading &&
      !paidItemsData.get("isLoading", false) &&
      !hasItems;
    const consolidationId = location.state && location.state.consolidationId;

    const refreshStripePaymentData = useCallback(() => {
      void refreshOutgoingPaymentData({
        getOutgoing,
        paymentItemsCount: paymentItemsData.get("items", List()).size,
        packingItemsCount: packingItemsData.get("items", List()).size,
      });
    }, [getOutgoing, packingItemsData, paymentItemsData]);

    const parcelId = params?.id;
    const paymentResult = params?.payment_result;
    const stripeReturnTarget = useMemo(() => {
      if (!parcelId || paymentResult !== "stripe-return") return undefined;
      return parcelId === "all"
        ? ({ flow: "purchase_all" } as const)
        : ({ flow: "purchase", consolidationId: parcelId } as const);
    }, [parcelId, paymentResult]);

    useStripePaymentReturn({
      isStripeReturn: paymentResult === "stripe-return",
      target: stripeReturnTarget,
      search: location.search,
      onSuccess: refreshStripePaymentData,
      onPending: () =>
        toast.info(t("common.stripeProcessing"), { icon: false }),
      onTerminalError: () =>
        toastResponseError(t("common.stripePaymentFailed")),
      onError: (error) => toastResponseError(error),
      onInvalidReturn: () =>
        toastResponseError(t("common.stripeInvalidReturn")),
      onReturnHandled: () => navigate("/parcels/outgoing", { replace: true }),
    });

    useEffect(() => {
      const query = queryString.parse(location.search);

      if (parcelId && paymentResult === "success") {
        if (isEmpty(query)) {
          if (parcelId === "all") {
            completeAllOverpay();
          } else {
            completeOverpayPayment({ id: parcelId });
          }
        }
        navigate("/parcels/outgoing", { replace: true });
        return;
      } else if (parcelId && paymentResult === "cancel") {
        toastResponseError(t("error.paymentWasCanceled"));
        navigate("/parcels/outgoing", { replace: true });
        return;
      }
    }, []);

    useEffect(() => {
      !allDeliveryMethods.size && getAllDeliveryMethods();

      if (consolidationId) {
        const parcel =
          paymentItemsData
            .get("items", List())
            .find((item: any) => item.get("id") === consolidationId) ||
          informationItemsData
            .get("items", List())
            .find((item: any) => item.get("id") === consolidationId) ||
          packingItemsData
            .get("items", List())
            .find((item: any) => item.get("id") === consolidationId) ||
          userCart
            .get("items", List())
            .find((item: any) => item.get("id") === consolidationId) ||
          List();
        const parcelState = parcel.get("state");

        const namePanel =
          (parcelState === "packed" && "WAITING_PAYMENT") ||
          (parcelState === "requesting" && "NOT_FINISHED") ||
          "IN_PROGRESS";

        openRightPanel(
          namePanel,
          Map({
            detailedItemID: consolidationId,
            returnPreviousPanel: namePanel,
            foundPackageId: location.state.shipmentId,
            shipments: userCart.getIn(["items", 0, "items"]),
            sku: userCart.getIn(["items", 0, "sku"]),
          }),
        );
        navigate(location.pathname, { state: undefined, replace: true });
      }
    }, []);

    const clickPayAll = useCallback(
      (event: MouseEvent) => {
        event.stopPropagation();
        const total = paymentItemsData.get("total");
        const items = paymentItemsData.get("items");
        const shouldFetchAll = parseInt(total, 10) !== items.size;
        const loadMoreData = {
          type: paymentItemsData.get("type"),
          params: {
            per_page: total,
            page: 1,
          },
        };

        if (shouldFetchAll) getOutgoing(Map(loadMoreData));

        openRightPanel("PAY_ALL", Map());
      },
      [paymentItemsData],
    );

    const inProgressItemsCount = packingItemsData.get("total") || "0";
    const informationItemsCount = informationItemsData.get("total") || "0";
    const paymentItemsCount = paymentItemsData.get("total") || "0";
    const paidItemsCount = paidItemsData.get("total") || "0";

    const inProgressHeaderTitle = `${t(
      "parcels.inProgress",
    )}: ${inProgressItemsCount}`;
    const waitingForInformationHeaderTitle = `${t(
      "parcels.waitingForInformation",
    )}: ${informationItemsCount}`;

    const waitingForPaymentHeaderTitle = `${t(
      "parcels.waitingForPayment",
    )}: ${paymentItemsCount}`;

    const paidItemsHeaderTitle = `${t("parcels.paymentPaid")}: ${paidItemsCount}`;

    const renderPayAllButton = () => {
      return (
        <PayAllHeaderButton color={ButtonColor.Secondary} onClick={clickPayAll}>
          {`${t("shipping.total")}`}
          <b>{` ${currencySymbol}${formatter.currency(paymentItemsData.get("totalPrice"))}`}</b>
        </PayAllHeaderButton>
      );
    };

    return (
      <OutgoingWrapper $isPanelOpened={isPanelOpened}>
        <Container fluid>
          <LoadingBar isLoading={isLoading} isCartLoading={isCartLoading} />
          <OutgoingSection
            isCollapseOpen
            data={userCart}
            fetchData={getCart}
            itemRenderer={(parcel: any, layoutTier: OutgoingRowLayoutTier) => (
              <NotFinished
                units={units}
                parcel={parcel}
                layoutTier={layoutTier}
                key={parcel.get("id")}
              />
            )}
          >
            <HeaderTitle>
              <Icon type={IconType.Pause} color="#ffaf5d" />
              {t("parcels.notFinished")}
            </HeaderTitle>
          </OutgoingSection>
          <OutgoingSection
            data={packingItemsData}
            fetchData={getOutgoing}
            countItemsPerSection={5}
            isCollapseOpen
            itemRenderer={(parcel: any, layoutTier: OutgoingRowLayoutTier) => (
              <InProgress
                units={units}
                parcel={parcel}
                layoutTier={layoutTier}
                key={parcel.get("id")}
              />
            )}
          >
            <HeaderTitle>
              <Icon type={IconType.Add} color="#3ebcf7" />
              {inProgressHeaderTitle}
            </HeaderTitle>
          </OutgoingSection>
          <OutgoingSection
            data={informationItemsData}
            fetchData={getOutgoing}
            isCollapseOpen
            itemRenderer={(parcel: any, layoutTier: OutgoingRowLayoutTier) => (
              <WaitingForInformation
                units={units}
                parcel={parcel}
                layoutTier={layoutTier}
                key={parcel.get("id")}
              />
            )}
          >
            <HeaderTitle>
              <Icon type={IconType.Exclamation} color="#d788ee" />
              {waitingForInformationHeaderTitle}
            </HeaderTitle>
          </OutgoingSection>
          <OutgoingSection
            data={paymentItemsData}
            fetchData={getOutgoing}
            isCollapseOpen
            itemRenderer={(parcel: any, layoutTier: OutgoingRowLayoutTier) => (
              <WaitingForPayment
                units={units}
                parcel={parcel}
                layoutTier={layoutTier}
                key={parcel.get("id")}
              />
            )}
          >
            <Row>
              <Col xs={7} sm={12}>
                <PaymentHeaderContent>
                  <HeaderTitle>
                    <Icon type={IconType.Money} color="#1fd983" />
                    {waitingForPaymentHeaderTitle}
                  </HeaderTitle>
                  <Hidden xs>{renderPayAllButton()}</Hidden>
                </PaymentHeaderContent>
              </Col>
              <Visible xs>
                <Col xs={5}>
                  <Row justify="end">
                    <Col xs={12}>{renderPayAllButton()}</Col>
                  </Row>
                </Col>
              </Visible>
            </Row>
          </OutgoingSection>
          <OutgoingSection
            data={paidItemsData}
            fetchData={getOutgoing}
            isCollapseOpen
            itemRenderer={(parcel: any, layoutTier: OutgoingRowLayoutTier) => (
              <Paid
                units={units}
                parcel={parcel}
                layoutTier={layoutTier}
                key={parcel.get("id")}
              />
            )}
          >
            <HeaderTitle>
              <Icon type={IconType.Money} color="#D25825" />
              {paidItemsHeaderTitle}
            </HeaderTitle>
          </OutgoingSection>
          {isSettledEmpty &&
            (isDemoEnabled ? <DemoOutgoing /> : <NoItemsPlaceholder />)}
        </Container>
      </OutgoingWrapper>
    );
  },
);

const withReducer = injectReducer({
  key: "outgoing",
  reducer,
});

const withConnect = connect(
  createStructuredSelector({
    paymentItemsData: outgoingSelectPaymentData,
    informationItemsData: outgoingSelectInformationData,
    packingItemsData: outgoingSelectPackingData,
    paidItemsData: outgoingSelectPaidData,
    isLoading: outgoingSelectLoading,
    isCartLoading: cartSelectLoading,
    isPanelOpened: panelSelectOpened,
    units: authSelectUnits,
    userCart: cartSelectForOutgoing,
    allDeliveryMethods: servicesSelectAllDelivery,
  }),
  {
    getCart,
    getOutgoing,
    getAllDeliveryMethods,
    completeAllOverpay,
    completeOverpayPayment,
    openRightPanel,
  },
);

const enhance = compose(withReducer, withConnect);

export default enhance(Outgoing);
